import { useEffect, useCallback, useState } from 'react'
import { createPortal } from 'react-dom'
import { X, ChevronLeft, ChevronRight, MessageCircle } from 'lucide-react'

export interface LightboxImage {
    url: string
    fullsize?: string
    alt?: string
}

interface ImageLightboxProps {
    images: LightboxImage[]
    initialIndex: number
    onClose: () => void
    onNavigateToPost?: () => void
}

export function ImageLightbox({
    images,
    initialIndex,
    onClose,
    onNavigateToPost,
}: ImageLightboxProps) {
    const [currentIndex, setCurrentIndex] = useState(initialIndex)
    const image = images[currentIndex]
    const hasMultiple = images.length > 1

    const goNext = useCallback(() => {
        setCurrentIndex(i => Math.min(i + 1, images.length - 1))
    }, [images.length])

    const goPrev = useCallback(() => {
        setCurrentIndex(i => Math.max(i - 1, 0))
    }, [])

    const handleKeyDown = useCallback(
        (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose()
            if (e.key === 'ArrowRight') goNext()
            if (e.key === 'ArrowLeft') goPrev()
        },
        [onClose, goNext, goPrev]
    )

    useEffect(() => {
        document.addEventListener('keydown', handleKeyDown)
        document.body.style.overflow = 'hidden'
        return () => {
            document.removeEventListener('keydown', handleKeyDown)
            document.body.style.overflow = ''
        }
    }, [handleKeyDown])

    if (!image) return null

    return createPortal(
        <div className="fixed inset-0 z-50 flex flex-col bg-black/95" onClick={onClose}>
            {/* Header: close button + counter */}
            <div className="flex items-center justify-between p-4">
                <span className="text-white/60 text-sm">
                    {hasMultiple ? `${currentIndex + 1} / ${images.length}` : ''}
                </span>
                <button
                    onClick={onClose}
                    className="text-white/80 hover:text-white transition-colors cursor-pointer"
                    aria-label="Close"
                >
                    <X size={28} />
                </button>
            </div>

            {/* Image + navigation arrows */}
            <div className="flex-1 flex items-center justify-center px-4 min-h-0 relative">
                {hasMultiple && currentIndex > 0 && (
                    <button
                        onClick={e => {
                            e.stopPropagation()
                            goPrev()
                        }}
                        className="absolute left-2 z-10 p-2 text-white/70 hover:text-white transition-colors cursor-pointer"
                        aria-label="Previous image"
                    >
                        <ChevronLeft size={32} />
                    </button>
                )}

                <img
                    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- an empty fullsize URL is invalid, same as missing
                    src={image.fullsize || image.url}
                    alt={image.alt ?? ''}
                    className="max-w-full max-h-full object-contain"
                    onClick={e => e.stopPropagation()}
                    onError={e => {
                        e.currentTarget.style.display = 'none'
                    }}
                />

                {hasMultiple && currentIndex < images.length - 1 && (
                    <button
                        onClick={e => {
                            e.stopPropagation()
                            goNext()
                        }}
                        className="absolute right-2 z-10 p-2 text-white/70 hover:text-white transition-colors cursor-pointer"
                        aria-label="Next image"
                    >
                        <ChevronRight size={32} />
                    </button>
                )}
            </div>

            {/* Alt text */}
            {image.alt && (
                <div
                    className="px-6 py-4 max-h-[30vh] overflow-y-auto"
                    onClick={e => e.stopPropagation()}
                >
                    <p className="text-white/90 text-sm leading-relaxed max-w-2xl mx-auto">
                        {image.alt}
                    </p>
                </div>
            )}

            {/* Bottom bar */}
            {onNavigateToPost && (
                <div
                    className="flex items-center justify-end px-4 py-3"
                    onClick={e => e.stopPropagation()}
                >
                    <button
                        onClick={onNavigateToPost}
                        className="text-white/80 hover:text-white transition-colors cursor-pointer"
                        aria-label="View thread"
                    >
                        <MessageCircle size={28} />
                    </button>
                </div>
            )}
        </div>,
        document.body
    )
}
